import React, { useMemo, useRef, useState, useEffect } from "react";
import { config as baseConfig, columnConfig } from "../config";
import {
  resolveTokenColor,
  getExtents,
  getStackedExtents,
  computeNiceTicks,
  formatYLabel,
  formatDataValue,
} from "../SupportFiles/chartUtils";
import {
  ClipPathRect,
  BackgroundRect,
  GridVertical,
  GridHorizontal,
  AxisLines,
} from "../SupportFiles/chartSections";
import { useUniqueComponentId } from "../SupportFiles/uniqueId";
import NoData from "../../Info/NoData";
function yScale(v, min, max, h, t, b, clampToMin) {
  const ch = Math.max(0, h - t - b);
  const vv = clampToMin ? Math.max(min, v) : v;
  const k = (vv - min) / (max - min);
  return t + ch - (isFinite(k) ? k : 0) * ch;
}
function yFromVal(v, h, p, clampToMin) {
  const ch = Math.max(0, h - p.top - p.bottom);
  const vv = clampToMin ? Math.max(p.min, v) : v;
  const k = (vv - p.min) / (p.max - p.min);
  return p.top + ch - (isFinite(k) ? k : 0) * ch;
}
function pathForCenters(data, n, centerXFn, h, p, clampToMin, curveType) {
  if (!data || data.length === 0) return "";
  if (curveType === "smooth" && data.length >= 2) {
    const pts = [];
    for (let i = 0; i < data.length; i++) {
      const x = centerXFn(i);
      const y = yFromVal(data[i], h, p, clampToMin);
      pts.push({ x, y });
    }
    let d = `M${pts[0].x},${pts[0].y}`;
    if (pts.length === 2) {
      d += ` L${pts[1].x},${pts[1].y}`;
      return d;
    }
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i > 0 ? i - 1 : i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2 < pts.length ? i + 2 : i + 1];
      const c1x = p1.x + (p2.x - p0.x) / 6;
      const c1y = p1.y + (p2.y - p0.y) / 6;
      const c2x = p2.x - (p3.x - p1.x) / 6;
      const c2y = p2.y - (p3.y - p1.y) / 6;
      d += ` C${c1x},${c1y} ${c2x},${c2y} ${p2.x},${p2.y}`;
    }
    return d;
  }
  let d = "";
  for (let i = 0; i < data.length; i++) {
    const x = centerXFn(i);
    const y = yFromVal(data[i], h, p, clampToMin);
    if (i === 0) d = `M${x},${y}`;
    else if (curveType === "stepline") {
      const prevY = yFromVal(data[i - 1], h, p, clampToMin);
      d += ` L${x},${prevY} L${x},${y}`;
    } else {
      d += ` L${x},${y}`;
    }
  }
  return d;
}
function areaPathForCenters(data, n, centerXFn, h, p, clampToMin, curveType) {
  if (!data || data.length === 0) return "";
  const top = pathForCenters(data, n, centerXFn, h, p, clampToMin, curveType);
  const firstX = centerXFn(0);
  const lastX = centerXFn(n - 1);
  const baseY = h - p.bottom;
  return `${top} L${lastX},${baseY} L${firstX},${baseY} Z`;
}

const ColumnChartNew = ({
  series = [{ name: "Series 1", data: [] }],
  categories = [],
  title,
  height = 360,
  width = 600,
  colors,
  dataLabelsEnabled = baseConfig?.showLabels?.show || false,
  edgePadding,
  padding: paddingProp,
  colorMap,
  stacked = columnConfig?.stacked || false,
  columnOptions,
  componentId,
  onHover,
}) => {
  const generatedId = useUniqueComponentId("charts-adv");
  const localId = componentId || generatedId;
  const basePad = (paddingProp ?? baseConfig.padding) || {
    top: 16,
    right: 12,
    bottom: 28,
    left: 40,
  };
  const svgW = typeof width === "number" ? width : Number(width) || 600;
  const svgH = typeof height === "number" ? height : Number(height) || 360;
  const pad = {
    top: basePad.top,
    right: basePad.right,
    bottom: basePad.bottom,
    left: basePad.left,
  };
  const axisLabelFontSizePx =
    typeof baseConfig.axis?.fontSize === "number"
      ? baseConfig.axis.fontSize
      : 12;
  const tickLabelGap = Math.max(8, Math.round(axisLabelFontSizePx * 0.6));
  const cornerRadius =
    typeof columnOptions?.radius === "number"
      ? Math.max(0, columnOptions.radius)
      : typeof columnConfig?.radius === "number"
        ? Math.max(0, columnConfig.radius)
        : 0;
  const axisLabelColor = resolveTokenColor(baseConfig.axis.labelColor);
  const axisBorderColor = resolveTokenColor(baseConfig.axis.color);
  const gridColor = resolveTokenColor(baseConfig.grid.color);
  const gridBg = resolveTokenColor(baseConfig.grid?.background);
  const clampData = true;
  const ePad =
    typeof edgePadding === "number"
      ? Math.max(0, edgePadding)
      : typeof baseConfig.axis?.edgePadding === "number"
        ? baseConfig.axis.edgePadding
        : 0;
  const scrollRef = useRef(null);
  const svgRef = useRef(null);
  const clipIdRef = useRef(`plotClip-${localId}`);
  const [hover, setHover] = useState(null);
  const [dragging, setDragging] = useState(false);
  const draggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollRef = useRef(0);
  const widthCfg =
    typeof columnOptions?.width === "number"
      ? Math.max(0, columnOptions.width)
      : typeof columnConfig?.width === "number"
        ? Math.max(0, columnConfig.width)
        : 0;
  const gapCfg =
    typeof columnOptions?.gap === "number"
      ? Math.max(0, columnOptions.gap)
      : typeof columnConfig?.gap === "number"
        ? Math.max(0, columnConfig.gap)
        : 0;
  const groupGapCfg =
    typeof columnOptions?.groupGap === "number"
      ? Math.max(0, columnOptions.groupGap)
      : typeof columnConfig?.groupGap === "number"
        ? Math.max(0, columnConfig.groupGap)
        : gapCfg;
  const visibleSeries = useMemo(
    () => series.filter((s) => !s.__projection),
    [series],
  );
  const innerNameToColorIndex = useMemo(() => {
    const map = new Map();
    for (const s of visibleSeries) {
      const key = s?.name;
      if (!key) continue;
      if (!map.has(key)) map.set(key, map.size);
    }
    return map;
  }, [visibleSeries]);
  const groupIndices = useMemo(() => {
    const set = new Set();
    for (const s of visibleSeries) {
      if (typeof s?.__group === "number" && isFinite(s.__group)) {
        set.add(Number(s.__group));
      }
    }
    return Array.from(set).sort((a, b) => a - b);
  }, [visibleSeries]);
  const groupedStacked = stacked && groupIndices.length > 0;
  const nVisible = Math.max(1, visibleSeries.length);
  const nBarsPerCategory = groupedStacked
    ? Math.max(1, groupIndices.length)
    : stacked
      ? 1
      : nVisible;
  const { min, max } = useMemo(() => {
    if (groupedStacked) {
      let maxV = 0;
      const nCatsLocal = Math.max(0, categories.length);
      for (let ci = 0; ci < nCatsLocal; ci += 1) {
        for (const g of groupIndices) {
          let sum = 0;
          for (const s of visibleSeries) {
            if (Number(s?.__group) !== g) continue;
            const vRaw = s?.data?.[ci];
            sum += vRaw != null && isFinite(vRaw) ? Number(vRaw) : 0;
          }
          if (sum > maxV) maxV = sum;
        }
      }
      return { min: 0, max: maxV };
    }
    if (stacked) {
      return getStackedExtents(visibleSeries, categories.length, true);
    }
    return getExtents(visibleSeries, true);
  }, [groupedStacked, groupIndices, visibleSeries, categories.length, stacked]);
  const plotHPre = Math.max(0, svgH - pad.top - pad.bottom);
  const tickTargetPx =
    typeof baseConfig.axis?.y?.tickTargetPx === "number"
      ? baseConfig.axis.y.tickTargetPx
      : 48;
  const interior =
    typeof baseConfig.axis?.y?.gridLines === "number"
      ? baseConfig.axis.y.gridLines
      : Math.max(2, Math.round(plotHPre / tickTargetPx));
  const {
    ticks: ticksY,
    step: tickStep,
    niceMin,
    niceMax,
  } = computeNiceTicks(min, max, interior, true);
  const p = {
    ...pad,
    min: niceMin,
    max: niceMax,
  };
  const axisYWidth =
    typeof baseConfig.axis?.y?.width === "number"
      ? Math.max(0, baseConfig.axis.y.width)
      : p.left;
  const axisXHeight =
    typeof baseConfig.axis?.x?.height === "number"
      ? Math.max(0, baseConfig.axis.x.height)
      : Math.max(24, axisLabelFontSizePx + 12);
  const nCats = categories.length;
  const desiredBandGap = gapCfg;
  const baseMinBand =
    typeof columnOptions?.minBandWidth === "number"
      ? Math.max(1, columnOptions.minBandWidth)
      : typeof columnConfig?.minBandWidth === "number"
        ? Math.max(1, columnConfig.minBandWidth)
        : Math.max(36, (widthCfg || 30) + 24);
  const requiredMinBand =
    widthCfg > 0
      ? Math.max(
          baseMinBand,
          Math.max(
            1,
            nBarsPerCategory * widthCfg +
              Math.max(0, (nBarsPerCategory - 1) * desiredBandGap) +
              groupGapCfg,
          ),
        )
      : baseMinBand;
  const minBandWidth = requiredMinBand;
  const containerW = Math.max(0, svgW);
  const contentW =
    p.left +
    p.right +
    2 * ePad +
    Math.max(0, minBandWidth * Math.max(nCats, 1));
  const scrollEnabled =
    baseConfig.axis?.scroll === "auto"
      ? contentW > containerW
      : !!baseConfig.axis?.scroll;
  const wEff = scrollEnabled ? contentW : containerW;
  const dash = baseConfig.grid?.dasharray || "3 3";
  const axisFontSize =
    typeof baseConfig.axis?.fontSize === "number"
      ? `${baseConfig.axis.fontSize}px`
      : undefined;
  const dataLabelFontSize =
    typeof baseConfig.showLabels?.fontSize === "number"
      ? `${baseConfig.showLabels.fontSize}px`
      : undefined;
  const resolvedColors = useMemo(
    () =>
      (colors || baseConfig.colors).map((c, i) => {
        const resolved =
          (colorMap && colorMap[i]) || resolveTokenColor(c) || "#3b82f6";
        return resolved;
      }),
    [colors, colorMap],
  );
  const bandGapCfg = gapCfg;
  const groupGapCfgLocal = groupGapCfg;
  const xMinPlot = p.left + ePad;
  const xMaxPlot = wEff - p.right - ePad;
  const cwPlot = Math.max(0, xMaxPlot - xMinPlot);
  const nCatsSafe = Math.max(categories.length, 1);
  const bandWidth = Math.max(1, Math.floor(cwPlot / nCatsSafe));
  const groupGap = Math.min(groupGapCfgLocal, Math.max(0, bandWidth - 1));
  const groupWidth = Math.max(1, bandWidth - groupGap);
  let bandGap = Math.min(
    bandGapCfg,
    Math.max(0, Math.floor(groupWidth / Math.max(nBarsPerCategory, 1)) - 1),
  );
  let barWidthCalc = Math.floor(
    (groupWidth - Math.max(0, (nBarsPerCategory - 1) * bandGap)) /
      nBarsPerCategory,
  );
  if (barWidthCalc < 1) {
    bandGap = 0;
    barWidthCalc = Math.max(1, Math.floor(groupWidth / nBarsPerCategory));
  }
  const maxBarWidth = Math.max(
    1,
    Math.floor(
      (groupWidth - Math.max(0, (nBarsPerCategory - 1) * bandGap)) /
        nBarsPerCategory,
    ),
  );
  const barWidth =
    widthCfg > 0 ? Math.min(widthCfg, maxBarWidth) : Math.max(1, barWidthCalc);
  const centerX = (i) => xMinPlot + bandWidth * i + bandWidth / 2;
  const groupCenterX = (i) => centerX(i);
  const totalBarsWidth = Math.max(
    0,
    nBarsPerCategory * barWidth + Math.max(0, (nBarsPerCategory - 1) * bandGap),
  );
  const leftOffset = Math.max(0, Math.floor((groupWidth - totalBarsWidth) / 2));
  const handleMove = (e) => {
    if (draggingRef.current) return;
    const r = scrollRef.current?.getBoundingClientRect();
    if (!r || categories.length < 1) return;
    const xMin = p.left + ePad;
    const xMax = wEff - p.right - ePad;
    const yMin = p.top;
    const yMax = svgH - p.bottom;
    const scrollLeft = scrollRef.current?.scrollLeft || 0;
    const x = e.clientX - r.left + scrollLeft;
    const y = e.clientY - r.top;
    if (x < xMin || x > xMax || y < yMin || y > yMax) {
      setHover(null);
      if (onHover) onHover(null);
      return;
    }
    const idx = Math.max(
      0,
      Math.min(categories.length - 1, Math.floor((x - xMin) / bandWidth)),
    );
    const cx = centerX(idx);
    const groupStartX = Math.round(cx - groupWidth / 2);
    let xp = cx;
    let groupSlot = 0;
    if (groupedStacked) {
      const slotW = barWidth + bandGap;
      const relX = x - (groupStartX + leftOffset);
      groupSlot = slotW > 0 ? Math.floor(relX / slotW) : 0;
      groupSlot = Math.max(0, Math.min(groupIndices.length - 1, groupSlot));
      xp = groupStartX + leftOffset + groupSlot * slotW + barWidth / 2;
    }
    setHover({ i: idx, x: xp });
    if (onHover) {
      const groupIdx = groupedStacked ? groupIndices[groupSlot] : null;
      const hoverSeries = groupedStacked
        ? visibleSeries.filter((s) => Number(s?.__group) === groupIdx)
        : visibleSeries;
      const values = hoverSeries.map((s) => s.data[idx]);
      const rows = groupedStacked
        ? hoverSeries.map((s, si) => {
            const innerColorIndex =
              typeof s?.__colorIdx === "number" && isFinite(s.__colorIdx)
                ? s.__colorIdx
                : typeof s?.name === "string"
                  ? (innerNameToColorIndex.get(s.name) ?? 0)
                  : 0;
            const color =
              resolvedColors[innerColorIndex % resolvedColors.length] ||
              "#3b82f6";
            return {
              name: s?.name || `Series ${si + 1}`,
              color,
              value: formatDataValue(s?.data?.[idx]),
            };
          })
        : undefined;
      const groupName =
        groupedStacked &&
        hoverSeries[0] &&
        typeof hoverSeries[0].__groupName === "string"
          ? hoverSeries[0].__groupName
          : "";
      onHover({
        index: idx,
        guidelineX: r.left + xp - scrollLeft,
        topY: r.top + p.top + 12,
        category: groupName
          ? `${categories[idx]} • ${groupName}`
          : categories[idx],
        values,
        rows,
        componentId: localId,
      });
    }
  };
  const handleLeave = () => {
    setHover(null);
    if (onHover) onHover(null);
  };
  const onPointerDown = (e) => {
    const t = scrollRef.current;
    if (!t) return;
    draggingRef.current = true;
    setDragging(true);
    dragStartXRef.current = e.clientX;
    dragStartScrollRef.current = t.scrollLeft || 0;
    if (e.currentTarget.setPointerCapture) {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {}
    }
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    const t = scrollRef.current;
    if (!t) return;
    const dx = e.clientX - dragStartXRef.current;
    const max = Math.max(0, t.scrollWidth - t.clientWidth);
    const next = Math.min(Math.max(0, dragStartScrollRef.current - dx), max);
    t.scrollLeft = next;
  };
  const onPointerUp = () => {
    draggingRef.current = false;
    setDragging(false);
  };
  const onTouchStart = (e) => {
    const t = scrollRef.current;
    if (!t) return;
    const touch = e.touches && e.touches[0];
    if (!touch) return;
    draggingRef.current = true;
    setDragging(true);
    dragStartXRef.current = touch.clientX;
    dragStartScrollRef.current = t.scrollLeft || 0;
  };
  const onTouchMove = (e) => {
    if (!draggingRef.current) return;
    e.preventDefault();
    const t = scrollRef.current;
    if (!t) return;
    const touch = e.touches && e.touches[0];
    if (!touch) return;
    const dx = touch.clientX - dragStartXRef.current;
    const max = Math.max(0, t.scrollWidth - t.clientWidth);
    const next = Math.min(Math.max(0, dragStartScrollRef.current - dx), max);
    t.scrollLeft = next;
  };
  const onTouchEnd = () => {
    draggingRef.current = false;
    setDragging(false);
  };
  const isEmpty = useMemo(() => {
    if (!Array.isArray(categories) || categories.length === 0) return true;
    if (!Array.isArray(visibleSeries) || visibleSeries.length === 0)
      return true;
    let hasAny = false;
    let hasNonZero = false;
    for (const s of visibleSeries) {
      const arr = Array.isArray(s?.data) ? s.data : [];
      if (arr.length > 0) hasAny = true;
      for (const v of arr) {
        const n = v != null && isFinite(v) ? Number(v) : 0;
        if (n !== 0) {
          hasNonZero = true;
          break;
        }
      }
      if (hasNonZero) break;
    }
    return !hasAny || !hasNonZero;
  }, [categories, visibleSeries]);
  if (isEmpty) {
    const noDataCfg = baseConfig?.noDataConfig || {};
    const showNoData =
      typeof noDataCfg.show === "boolean" ? noDataCfg.show : true;
    return (
      <div
        className="chartWrapper w-full h-full flex"
        style={{
          width: typeof width === "number" ? `${width}px` : width,
          height: typeof height === "number" ? `${height}px` : height,
        }}
      >
        {showNoData ? (
          <NoData
            title={noDataCfg.title}
            desc={noDataCfg.desc}
            color={noDataCfg.color}
            noImage={noDataCfg.noImage}
          />
        ) : (
          <div className="w-full h-full relative p-6">
            <svg
              className="w-full h-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <rect x="0" y="0" width="100" height="100" fill="#ffffff" />
              {[20, 35, 50, 65, 80].map((y) => (
                <line
                  key={`g-${y}`}
                  x1="6"
                  y1={y}
                  x2="98"
                  y2={y}
                  stroke="#f3f4f6"
                  strokeWidth="1"
                />
              ))}
              <line x1="6" y1="88" x2="98" y2="88" stroke="#e5e7eb" />
              <line x1="6" y1="12" x2="6" y2="88" stroke="#e5e7eb" />
              {[
                { x: 14, w: 10, h: 36 },
                { x: 28, w: 10, h: 52 },
                { x: 42, w: 10, h: 28 },
                { x: 56, w: 10, h: 46 },
                { x: 70, w: 10, h: 22 },
                { x: 84, w: 10, h: 40 },
              ].map((b, i) => (
                <g key={`b-${i}`}>
                  <rect
                    x={b.x}
                    y={88 - b.h}
                    width={b.w}
                    height={b.h}
                    fill="#e5e7eb"
                    rx="1.5"
                  />
                  <rect
                    x={b.x}
                    y="91"
                    width={b.w}
                    height="4"
                    fill="#f3f4f6"
                    rx="1.5"
                  />
                </g>
              ))}
            </svg>
            <div className="absolute bottom-4 left-0 right-0 text-center text-sm text-gray-500">
              No data available
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="chartWrapper w-full h-full grid grid-cols-[auto_1fr]">
      <div className="yAxis h-full">
        <svg width={axisYWidth} height={svgH}>
          <g>
            {ticksY.map((tv, i) => {
              const y = yScale(tv, p.min, p.max, svgH, p.top, p.bottom, false);
              return (
                <text
                  key={`yl-${i}`}
                  x={axisYWidth - tickLabelGap}
                  y={y + 4}
                  fill={axisLabelColor}
                  fontSize={axisFontSize}
                  textAnchor="end"
                >
                  {formatYLabel(tv, tickStep)}
                </text>
              );
            })}
          </g>
        </svg>
      </div>
      <div className="chartContainer flex flex-col" style={{ minWidth: 0 }}>
        <div
          ref={scrollRef}
          style={{
            overflowX: scrollEnabled ? "auto" : "hidden",
            overflowY: "hidden",
            width: containerW,
            cursor: scrollEnabled
              ? dragging
                ? "grabbing"
                : "grab"
              : "default",
            userSelect: dragging ? "none" : "auto",
            touchAction: "none",
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <svg
            ref={svgRef}
            width={wEff}
            height={svgH}
            onMouseMove={handleMove}
            onMouseLeave={handleLeave}
          >
            <ClipPathRect
              id={clipIdRef.current}
              x={p.left}
              y={p.top}
              width={wEff - p.left - p.right}
              height={svgH - p.top - p.bottom}
            />
            <BackgroundRect
              x={p.left}
              y={p.top}
              width={wEff - p.left - p.right}
              height={svgH - p.top - p.bottom}
              fill={gridBg}
            />
            <g clipPath={`url(#${clipIdRef.current})`}>
              {baseConfig.grid.horizontal && (
                <GridHorizontal
                  ys={ticksY
                    .filter((_, idx) => idx > 0 && idx < ticksY.length - 1)
                    .map((tv) =>
                      yScale(tv, p.min, p.max, svgH, p.top, p.bottom, false),
                    )}
                  left={p.left}
                  right={wEff - p.right}
                  color={gridColor}
                  dash={dash}
                  strokeWidth={baseConfig.grid.strokeWidth || 1}
                />
              )}
              {baseConfig.grid.vertical && (
                <GridVertical
                  xs={categories.map((_, i) => centerX(i))}
                  top={p.top}
                  bottom={svgH - p.bottom}
                  color={gridColor}
                  dash={dash}
                  strokeWidth={baseConfig.grid.strokeWidth || 1}
                />
              )}
            </g>
            <g>
              <AxisLines
                xAxis={{
                  x1: p.left,
                  x2: wEff - p.right,
                  y: svgH - p.bottom,
                }}
                yAxis={{ x: p.left, y1: p.top, y2: svgH - p.bottom }}
                color={axisBorderColor}
              />
            </g>
            <g clipPath={`url(#${clipIdRef.current})`}>
              {series.map((s, si) => {
                if (!s.__projection) return null;
                const color =
                  (colorMap && colorMap[(s && s.__idx) ?? si]) ||
                  resolvedColors[
                    ((s && s.__idx) ?? si) % resolvedColors.length
                  ] ||
                  "#6b7280";
                const curveType = baseConfig.projection?.curve || "straight";
                const showArea = !!baseConfig?.projection?.area?.show;
                const dArea = showArea
                  ? areaPathForCenters(
                      s.data,
                      s.data.length,
                      centerX,
                      svgH,
                      p,
                      true,
                      curveType,
                    )
                  : "";
                const dProj = pathForCenters(
                  s.data,
                  s.data.length,
                  centerX,
                  svgH,
                  p,
                  true,
                  curveType,
                );
                const dashProj =
                  baseConfig.projection?.dasharray ||
                  baseConfig.grid?.dasharray ||
                  "3 3";
                return (
                  <g key={`proj-${si}`}>
                    {dArea && (
                      <path
                        d={dArea}
                        fill={resolveTokenColor(color)}
                        opacity={
                          typeof baseConfig?.projection?.area?.fill?.opacity ===
                          "number"
                            ? baseConfig.projection.area.fill.opacity
                            : 0.1
                        }
                        clipPath={`url(#${clipIdRef.current})`}
                      />
                    )}
                    <path
                      d={dProj}
                      fill="none"
                      stroke={resolveTokenColor(color)}
                      strokeWidth={
                        typeof baseConfig.projection?.strokeWidth === "number"
                          ? baseConfig.projection.strokeWidth
                          : 1
                      }
                      strokeDasharray={dashProj}
                      strokeLinecap={baseConfig.projection?.lineCap || "round"}
                    />
                  </g>
                );
              })}
            </g>
            <g clipPath={`url(#${clipIdRef.current})`}>
              {categories.map((cat, ci) => {
                const cx = centerX(ci);
                const groupStartX = Math.round(cx - groupWidth / 2);
                return (
                  <g key={`g-${ci}`}>
                    {!stacked &&
                      visibleSeries.map((s, si) => {
                        const color =
                          (colorMap && colorMap[(s && s.__idx) ?? si]) ||
                          resolvedColors[
                            ((s && s.__idx) ?? si) % resolvedColors.length
                          ] ||
                          "#3b82f6";
                        const v = s.data[ci];
                        if (v == null) return null;
                        const y = yScale(
                          v,
                          p.min,
                          p.max,
                          svgH,
                          p.top,
                          p.bottom,
                          clampData,
                        );
                        const baseY = svgH - p.bottom;
                        const x =
                          groupStartX + leftOffset + si * (barWidth + bandGap);
                        const h = Math.max(0, baseY - y);
                        return (
                          <React.Fragment key={`frag-${ci}-${si}`}>
                            <rect
                              x={x}
                              y={y}
                              width={barWidth}
                              height={h}
                              rx={cornerRadius}
                              ry={cornerRadius}
                              fill={color}
                            />
                            {dataLabelsEnabled && h > 12 && (
                              <text
                                x={x + barWidth / 2}
                                y={y - 4}
                                fill={
                                  baseConfig.showLabels?.colorMode ===
                                    "single" && baseConfig.showLabels?.color
                                    ? resolveTokenColor(
                                        baseConfig.showLabels.color,
                                      )
                                    : color
                                }
                                fontSize={dataLabelFontSize}
                                textAnchor="middle"
                              >
                                {formatDataValue(v)}
                              </text>
                            )}
                          </React.Fragment>
                        );
                      })}
                    {stacked &&
                      groupedStacked &&
                      (() => {
                        const baseY = yScale(
                          0,
                          p.min,
                          p.max,
                          svgH,
                          p.top,
                          p.bottom,
                          clampData,
                        );
                        return (
                          <React.Fragment>
                            {groupIndices.map((g, gi) => {
                              let cum = 0;
                              const x =
                                groupStartX +
                                leftOffset +
                                gi * (barWidth + bandGap);
                              const clipId = `clip-${localId}-v-${ci}-${g}`;
                              const groupSeries = visibleSeries.filter(
                                (s) => Number(s?.__group) === g,
                              );
                              const sumCat = groupSeries.reduce((acc, s) => {
                                const vRaw = s?.data?.[ci];
                                return (
                                  acc +
                                  (vRaw != null && isFinite(vRaw)
                                    ? Number(vRaw)
                                    : 0)
                                );
                              }, 0);
                              const topY = yScale(
                                sumCat,
                                p.min,
                                p.max,
                                svgH,
                                p.top,
                                p.bottom,
                                clampData,
                              );
                              const totalH = Math.max(0, baseY - topY);
                              return (
                                <React.Fragment key={`gs-${ci}-${g}`}>
                                  <defs>
                                    <clipPath id={clipId}>
                                      <rect
                                        x={x}
                                        y={topY}
                                        width={barWidth}
                                        height={totalH}
                                        rx={cornerRadius}
                                        ry={cornerRadius}
                                      />
                                    </clipPath>
                                  </defs>
                                  {groupSeries.map((s, si) => {
                                    const innerColorIndex =
                                      typeof s?.__colorIdx === "number" &&
                                      isFinite(s.__colorIdx)
                                        ? s.__colorIdx
                                        : typeof s?.name === "string"
                                          ? (innerNameToColorIndex.get(
                                              s.name,
                                            ) ?? 0)
                                          : 0;
                                    const color =
                                      resolvedColors[
                                        innerColorIndex % resolvedColors.length
                                      ] || "#3b82f6";
                                    const vRaw = s?.data?.[ci];
                                    if (vRaw == null || !isFinite(vRaw))
                                      return null;
                                    const v = Number(vRaw);
                                    const yTop = yScale(
                                      cum + v,
                                      p.min,
                                      p.max,
                                      svgH,
                                      p.top,
                                      p.bottom,
                                      clampData,
                                    );
                                    const yBottom = yScale(
                                      cum,
                                      p.min,
                                      p.max,
                                      svgH,
                                      p.top,
                                      p.bottom,
                                      clampData,
                                    );
                                    const h = Math.max(0, yBottom - yTop);
                                    cum += v;
                                    return (
                                      <rect
                                        key={`gstack-${ci}-${g}-${si}`}
                                        x={x}
                                        y={yTop}
                                        width={barWidth}
                                        height={h}
                                        rx={0}
                                        ry={0}
                                        fill={color}
                                        clipPath={`url(#${clipId})`}
                                      />
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })}
                          </React.Fragment>
                        );
                      })()}
                    {stacked &&
                      !groupedStacked &&
                      (() => {
                        let cum = 0;
                        const stackWidth =
                          widthCfg > 0
                            ? Math.min(widthCfg, groupWidth)
                            : groupWidth;
                        const x = Math.round(cx - stackWidth / 2);
                        const sumCat = visibleSeries.reduce((acc, s) => {
                          const vRaw = s.data[ci];
                          return (
                            acc +
                            (vRaw != null && isFinite(vRaw) ? Number(vRaw) : 0)
                          );
                        }, 0);
                        const topY = yScale(
                          sumCat,
                          p.min,
                          p.max,
                          svgH,
                          p.top,
                          p.bottom,
                          clampData,
                        );
                        const baseY = yScale(
                          0,
                          p.min,
                          p.max,
                          svgH,
                          p.top,
                          p.bottom,
                          clampData,
                        );
                        const totalH = Math.max(0, baseY - topY);
                        const clipId = `clip-${localId}-v-${ci}`;
                        return (
                          <React.Fragment>
                            <defs>
                              <clipPath id={clipId}>
                                <rect
                                  x={x}
                                  y={topY}
                                  width={stackWidth}
                                  height={totalH}
                                  rx={cornerRadius}
                                  ry={cornerRadius}
                                />
                              </clipPath>
                            </defs>
                            {visibleSeries.map((s, si) => {
                              const color =
                                (colorMap && colorMap[(s && s.__idx) ?? si]) ||
                                resolvedColors[
                                  ((s && s.__idx) ?? si) % resolvedColors.length
                                ] ||
                                "#3b82f6";
                              const vRaw = s.data[ci];
                              if (vRaw == null || !isFinite(vRaw)) return null;
                              const v = Number(vRaw);
                              const yTop = yScale(
                                cum + v,
                                p.min,
                                p.max,
                                svgH,
                                p.top,
                                p.bottom,
                                clampData,
                              );
                              const yBottom = yScale(
                                cum,
                                p.min,
                                p.max,
                                svgH,
                                p.top,
                                p.bottom,
                                clampData,
                              );
                              const h = Math.max(0, yBottom - yTop);
                              cum += v;
                              return (
                                <React.Fragment key={`stack-${ci}-${si}`}>
                                  <rect
                                    x={x}
                                    y={yTop}
                                    width={stackWidth}
                                    height={h}
                                    rx={0}
                                    ry={0}
                                    fill={color}
                                    clipPath={`url(#${clipId})`}
                                  />
                                  {dataLabelsEnabled && h > 12 && (
                                    <text
                                      x={x + stackWidth / 2}
                                      y={yTop - 4}
                                      fill={
                                        baseConfig.showLabels?.colorMode ===
                                          "single" &&
                                        baseConfig.showLabels?.color
                                          ? resolveTokenColor(
                                              baseConfig.showLabels.color,
                                            )
                                          : color
                                      }
                                      fontSize={dataLabelFontSize}
                                      textAnchor="middle"
                                    >
                                      {formatDataValue(v)}
                                    </text>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </React.Fragment>
                        );
                      })()}
                  </g>
                );
              })}
            </g>
            {hover && (
              <g clipPath={`url(#${clipIdRef.current})`}>
                <line
                  x1={hover.x}
                  x2={hover.x}
                  y1={p.top}
                  y2={svgH - p.bottom}
                  stroke={gridColor}
                  strokeDasharray={dash}
                />
              </g>
            )}
            {title && (
              <text
                x={p.left}
                y={p.top - 6}
                fill={axisLabelColor}
                fontSize={axisFontSize}
              >
                {title}
              </text>
            )}
          </svg>
          <svg width={wEff} height={axisXHeight}>
            <g>
              {categories.map((c, i) => {
                const x = groupCenterX(i);
                return (
                  <text
                    key={`cx-${i}`}
                    x={x}
                    y={axisXHeight - 6}
                    fill={axisLabelColor}
                    fontSize={axisFontSize}
                    textAnchor="middle"
                  >
                    {c}
                  </text>
                );
              })}
            </g>
          </svg>
        </div>
      </div>
    </div>
  );
};

export default ColumnChartNew;
